/**
 * ResetPasswordPage — Saisie du nouveau mot de passe via token email.
 *
 * Refonte R5 (mai 2026) :
 *   - LogoMark ShieldCheck accent tech au-dessus du titre
 *   - Validation visuelle inline avec dots success/neutral (au lieu de ✓/○ texte)
 *   - Card glass + Alert avec icône
 *
 * Logique métier 100% préservée : token via searchParams, validation 12 chars + match,
 * gestion EXPIRED/INVALID_RESET_TOKEN avec lien réémission, redirect /connexion?reset=1.
 */

import { useState, useMemo, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Field, Card, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { PublicLayout } from '@/components/PublicLayout';
import { useAuth } from '@/lib/auth-context';
import { ApiException } from '@/lib/api';
import { AlertCircle, ArrowLeft, ShieldCheck, CheckCircle2, Circle } from 'lucide-react';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { resetPassword } = useAuth();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get('token') ?? '', [params]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Indicateur de longueur très simple côté client (pas zxcvbn pour pas alourdir le bundle —
  // la validation forte est côté serveur, source de vérité)
  const lengthOk = password.length >= 12;
  const matchOk = password.length > 0 && password === confirm;

  if (!token) {
    return (
      <PublicLayout maxWidth="sm">
        <div className="text-center space-y-5">
          <div className="flex justify-center">
            <div
              className="inline-grid h-12 w-12 place-items-center rounded-r-xl bg-gradient-to-b from-urgent to-urgent/70 text-white shadow-glow-urgent"
              style={{ boxShadow: '0 6px 20px -8px oklch(var(--c-urgent) / 0.55), inset 0 1px 0 oklch(1 0 0 / 0.25)' }}
              aria-hidden="true"
            >
              <AlertCircle className="h-6 w-6" />
            </div>
          </div>
          <div>
            <SectionEyebrow as="div">Erreur</SectionEyebrow>
            <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
              Lien invalide
            </h1>
          </div>
          <p className="text-sm text-ink-muted leading-relaxed">
            Ce lien de réinitialisation est mal formé. Demandez-en un nouveau depuis la page de
            connexion.
          </p>
          <div className="pt-2">
            <Link to="/mot-de-passe-oublie">
              <Button variant="primary">Demander un nouveau lien</Button>
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setErrorCode(null);

    if (!lengthOk) {
      setError('Le mot de passe doit faire au moins 12 caractères.');
      return;
    }
    if (!matchOk) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, password);
      navigate('/connexion?reset=1', { replace: true });
    } catch (err) {
      if (err instanceof ApiException) {
        setErrorCode(err.payload.code ?? null);
        setError(err.payload.error);
      } else {
        setError('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <PublicLayout maxWidth="sm">
      <div className="space-y-6">
        <div className="text-center">
          <div className="flex justify-center">
            <LogoMark icon={ShieldCheck} accent="tech" size="lg" aria-label="Nouveau mot de passe" />
          </div>
          <SectionEyebrow as="div" className="mt-4">Sécurité</SectionEyebrow>
          <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
            Nouveau mot de passe
          </h1>
          <p className="mt-2 text-sm text-ink-muted">
            Choisissez un mot de passe long, unique et que vous n'utilisez nulle part ailleurs.
          </p>
        </div>

        <Card variant="glass" padded>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <Alert variant="error" icon={AlertCircle}>
                {error}
                {(errorCode === 'EXPIRED_RESET_TOKEN' || errorCode === 'INVALID_RESET_TOKEN') && (
                  <>
                    {' '}
                    <Link
                      to="/mot-de-passe-oublie"
                      className="font-medium underline hover:no-underline"
                    >
                      Demander un nouveau lien
                    </Link>
                  </>
                )}
              </Alert>
            )}

            <Field
              label="Nouveau mot de passe"
              htmlFor="password"
              hint="Au moins 12 caractères. Évitez les mots du dictionnaire et les informations personnelles."
              required
            >
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                minLength={12}
                className="h-12 sm:h-11"
              />
            </Field>

            <Field label="Confirmer le mot de passe" htmlFor="confirm" required>
              <Input
                id="confirm"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                className="h-12 sm:h-11"
                error={confirm.length > 0 && !matchOk}
              />
            </Field>

            {/* Indicateurs visuels — checks success / circles neutral selon état */}
            <ul className="space-y-1.5 text-xs pt-1">
              <li className={`inline-flex items-center gap-2 ${lengthOk ? 'text-success' : 'text-ink-subtle'}`}>
                {lengthOk ? (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Circle className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Au moins 12 caractères
              </li>
              <li className={`flex items-center gap-2 ${matchOk ? 'text-success' : 'text-ink-subtle'}`}>
                {matchOk ? (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Circle className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Les deux mots de passe correspondent
              </li>
            </ul>

            <Button
              type="submit"
              loading={loading}
              disabled={!lengthOk || !matchOk}
              className="w-full"
              size="lg"
            >
              Enregistrer le nouveau mot de passe
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-ink-muted">
          <Link
            to="/connexion"
            className="inline-flex items-center gap-1 font-medium text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md px-1"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Retour à la connexion
          </Link>
        </p>
      </div>
    </PublicLayout>
  );
}
